import { useEffect, useState } from "react";
import { api, formatRupiah, errMsg } from "@/lib/api";
import { Camera, Plus, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PettyCash() {
  const [holders, setHolders] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState("expense"); // expense, topup, recon
  const [form, setForm] = useState({ amount: 0, category: "Bunga", description: "", cash_holder: "", receipt_photo: "" });
  const [topup, setTopup] = useState({ cash_holder_id: "", amount: 0, note: "" });
  const [recon, setRecon] = useState({ cash_holder_id: "", physical_balance: 0, note: "" });
  const [err, setErr] = useState("");
  const { user } = useAuth();
  const canTopup = ["Owner", "Finance Admin"].includes(user?.role);

  async function load() {
    const [h, me] = await Promise.all([api.get("/pettycash"), api.get("/auth/me")]);
    setHolders(h.data);
    setForm(f => ({ ...f, cash_holder: me.data.id }));
    setUsers(h.data);
  }
  useEffect(() => { load(); }, []);

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, receipt_photo: reader.result });
    reader.readAsDataURL(file);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      if (modal === "expense") {
        if (!form.receipt_photo) { setErr("Foto nota wajib"); return; }
        await api.post("/expenses", {
          amount: Number(form.amount), category: form.category,
          description: form.description, cash_holder: form.cash_holder,
          receipt_photo: form.receipt_photo
        });
      } else if (modal === "topup") {
        await api.post("/pettycash/topup", { ...topup, amount: Number(topup.amount) });
      } else {
        await api.post("/pettycash/reconcile", { ...recon, physical_balance: Number(recon.physical_balance) });
      }
      setOpen(false); setErr("");
      await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  return (
    <div className="p-4 sm:p-8 space-y-5">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Petty Cash</h1>
          <p className="text-sm text-[#536263]">Pengajuan pengeluaran lapangan + rekonsiliasi harian</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setModal("expense"); setOpen(true); }} className="btn-primary" data-testid="submit-expense-btn"><Plus size={16} />Ajukan Pengeluaran</button>
          {canTopup && <button onClick={() => { setModal("topup"); setOpen(true); }} className="btn-copper" data-testid="topup-btn">Top Up</button>}
          <button onClick={() => { setModal("recon"); setOpen(true); }} className="btn-copper" data-testid="recon-btn">Rekonsiliasi</button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pettycash-holders">
        {holders.map(h => (
          <div key={h.user_id} className="card-editorial p-5">
            <div className="text-xs uppercase tracking-wider text-[#B87333]">{h.role}</div>
            <h3 className="font-serif-display text-lg font-semibold">{h.name}</h3>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Top-up:</span><span className="font-mono-num text-emerald-700">{formatRupiah(h.topups)}</span></div>
              <div className="flex justify-between"><span>Terpakai:</span><span className="font-mono-num text-red-700">{formatRupiah(h.spent)}</span></div>
              <div className="flex justify-between font-bold border-t border-[#E2DDD3] pt-2 mt-2">
                <span>Saldo Sistem:</span>
                <span className={`font-mono-num ${h.system_balance < 0 ? "text-red-700" : "text-[#0F5257]"}`}>{formatRupiah(h.system_balance)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid={`pettycash-form-${modal}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl font-bold">
                {modal === "expense" ? "Ajukan Pengeluaran" : modal === "topup" ? "Top Up Kas" : "Rekonsiliasi Kas"}
              </h3>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            {modal === "expense" && (
              <>
                <input required type="number" placeholder="Nominal" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11">
                  {["Bunga","Properti","Transport","Konsumsi","Upah","Lainnya"].map(x => <option key={x}>{x}</option>)}
                </select>
                <input required placeholder="Deskripsi" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
                <label className="flex items-center gap-3 border-2 border-dashed border-[#E2DDD3] rounded-lg p-3 cursor-pointer">
                  <Camera size={20} className="text-[#B87333]" />
                  <span className="text-sm text-[#536263]">{form.receipt_photo ? "Foto nota tersimpan" : "Upload Foto Nota (wajib)"}</span>
                  <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="receipt-upload" />
                </label>
              </>
            )}
            {modal === "topup" && (
              <>
                <select required value={topup.cash_holder_id} onChange={e => setTopup({...topup, cash_holder_id: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11">
                  <option value="">Pilih Pemegang Kas</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
                </select>
                <input required type="number" placeholder="Nominal" value={topup.amount} onChange={e => setTopup({...topup, amount: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
                <input placeholder="Catatan" value={topup.note} onChange={e => setTopup({...topup, note: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
              </>
            )}
            {modal === "recon" && (
              <>
                <select required value={recon.cash_holder_id} onChange={e => setRecon({...recon, cash_holder_id: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11">
                  <option value="">Pilih Pemegang Kas</option>
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                </select>
                <input required type="number" placeholder="Saldo Fisik (Rp)" value={recon.physical_balance} onChange={e => setRecon({...recon, physical_balance: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
                <input placeholder="Catatan" value={recon.note} onChange={e => setRecon({...recon, note: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
              </>
            )}
            {err && <div className="text-sm text-red-700">{err}</div>}
            <button type="submit" className="btn-primary w-full" data-testid="pettycash-submit">Simpan</button>
          </form>
        </div>
      )}
    </div>
  );
}
